import { useMemo } from "react";
import { useRemoteResource } from "./useRemoteResource";
import { useScoreApi, unwrap } from "../api/scoreApi";

// Card #2147 — the day spine's `placed` marker for an entire ring, in ONE
// call (score-api's GET /class-results-summary?showId=&ring=, docs/score/
// announcer-ui-redesign.md §10 Gap 1: "today's per-class
// useClassResults(classId) is one poll per class... a 20-class ring means
// 20 requests every RESULTS_POLL_MS"). This hook is the fix: it rides the
// SAME shared entity-refresh beat as useRidesForRing/useBreaksForRing/
// useClassesForShow (`nonce` == OrderOfGo's entityTick) instead of running
// a poller of its own, and it is the ONE call for the whole ring — no
// per-class fan-out, ever, regardless of how many classes the ring holds.
//
// Returns a classId -> { classId, status, placedRideCount } map (score-api's
// row shape, CLASS_RESULTS_STATUSES.PLACED/NOT_PLACED from score-common) so
// callers do a single object lookup per class row instead of scanning the
// raw array on every render. The route ships no placings payload (score-api
// results.js's own comment on the route) — this is a cheap status bit, not a
// substitute for useClassResults' per-class detail fetch.
export function useClassResultsSummary(showId, ring, nonce = 0) {
  const api = useScoreApi();
  const { data, error, refetch, refreshing } = useRemoteResource(
    async () => {
      if (!showId || !ring) return [];
      return unwrap(await api.results.getClassResultsSummary(showId, ring)) ?? [];
    },
    [api, showId, ring, nonce],
    [showId, ring],
    [],
  );

  const summaryByClassId = useMemo(() => {
    const byId = {};
    for (const row of data || []) {
      if (row?.classId) byId[row.classId] = row;
    }
    return byId;
  }, [data]);

  return { summaryByClassId, error, refetch, refreshing };
}

export default useClassResultsSummary;
